import { type ApolloError } from '@apollo/client'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useUpdateCartQuantity } from './useUpdateCartQuantity'

/**
 * How long a burst of taps may keep growing before the quantity is committed.
 * Long enough that a shopper tapping `+` twenty times sends one mutation, short
 * enough that the cart is honest by the time they look at it.
 */
const SETTLE_MS = 400

export interface UseDebouncedCartQuantity {
  /**
   * Absolute quantities the shopper has chosen but the cart has not confirmed
   * yet, by sku. Overlay these on the cart's own quantities when rendering —
   * `{ ...committedBySku, ...pendingBySku }` — so every tap is visible
   * immediately even though the mutation is still waiting for the burst to end.
   */
  pendingBySku: Record<string, number>
  /** Set a line's quantity. A burst of calls collapses into one mutation carrying the last value. */
  changeQuantity: (sku: string, quantity: number) => void
  error: ApolloError | undefined
}

/**
 * Cart quantities with the arithmetic done locally and the network told once.
 *
 * Twenty taps of `+` used to be twenty `cartAddProduct` mutations racing each
 * other. Here every tap only moves a number in state; a per-sku timer waits for
 * the burst to end and then commits the final quantity in a single mutation
 * with `replace: true` — absolute, so a lost or re-ordered intermediate step
 * cannot corrupt the line.
 *
 * A pending entry survives its own flush until the mutation settles (so the UI
 * never flickers back to the stale committed quantity) and is dropped when the
 * response lands — by then the cache holds the same number. If the mutation
 * fails, dropping the entry reverts the UI to the cart's truth, and `error`
 * says so. Taps that arrive while a flush is in flight simply re-arm the
 * timer; the newer target wins because it is sent absolutely.
 *
 * Unmounting mid-burst flushes immediately: a shopper who taps and navigates
 * straight to the cart must not lose the taps.
 */
export const useDebouncedCartQuantity = (): UseDebouncedCartQuantity => {
  const { updateQuantity, error } = useUpdateCartQuantity()

  const [pendingBySku, setPendingBySku] = useState<Record<string, number>>({})
  // The timers and a mirror of the pending map, readable from timer callbacks
  // and the unmount cleanup without chasing a stale closure.
  const pendingRef = useRef<Record<string, number>>({})
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const setPending = useCallback((mutatePending: (prev: Record<string, number>) => Record<string, number>) => {
    pendingRef.current = mutatePending(pendingRef.current)
    setPendingBySku(pendingRef.current)
  }, [])

  const flush = useCallback(
    (sku: string) => {
      timers.current.delete(sku)
      const target = pendingRef.current[sku]
      if (target === undefined) return
      void Promise.resolve(updateQuantity(sku, target)).then(() => {
        // Keep the entry if the shopper kept tapping while this was in flight —
        // the re-armed timer owns the newer value and will flush it.
        if (timers.current.has(sku) || pendingRef.current[sku] !== target) return
        setPending((prev) => {
          const rest = { ...prev }
          delete rest[sku]
          return rest
        })
      })
    },
    [updateQuantity, setPending],
  )

  const changeQuantity = useCallback(
    (sku: string, quantity: number) => {
      setPending((prev) => ({ ...prev, [sku]: quantity }))
      const armed = timers.current.get(sku)
      if (armed !== undefined) clearTimeout(armed)
      timers.current.set(
        sku,
        setTimeout(() => flush(sku), SETTLE_MS),
      )
    },
    [flush, setPending],
  )

  // Leaving the screen commits what the timers were still holding. Only armed
  // timers flush: a pending entry without one is already in flight.
  useEffect(() => {
    const armedTimers = timers.current
    return () => {
      for (const [sku, timer] of armedTimers) {
        clearTimeout(timer)
        const target = pendingRef.current[sku]
        if (target !== undefined) void updateQuantity(sku, target)
      }
      armedTimers.clear()
    }
  }, [updateQuantity])

  return { pendingBySku, changeQuantity, error }
}

/**
 * How many units the pending map adds to (or removes from) the committed cart —
 * what a cart badge needs so it moves with the taps instead of with the network.
 */
export const pendingUnitsDelta = (
  pendingBySku: Record<string, number>,
  committedBySku: Record<string, number>,
): number =>
  Object.entries(pendingBySku).reduce(
    (sum, [sku, target]) => sum + target - (committedBySku[sku] ?? 0),
    0,
  )
